import React, { useState, useEffect } from "react";
import { Search, Plus } from "lucide-react";
import api from "../../../services/api";
import Navbar from "../../../components/Navbar";
import ProjectCard from "../../../components/ProjectCard/ProjectCard";
import "./ManageProjects.css";

const ManageProjects = ({ role, onProjectClick, onCreateProject }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await api.get("/projects");

        if (Array.isArray(response.data)) {
          const formattedProjects = response.data.map((project) => {
            return {
              ...project,
              project_id: project.project_id || project.id,
              name: project.title || "Unnamed Project",
              description: project.description || "",
              status: project.status || "pending",
              user_name: project.user_name || "Unknown User",
              start_date: project.start_date || null,
              clientName: project.clientname || "",
              project_details: {
                projectName: project.title || "Unnamed Project",
                description: project.description || "",
                createdBy: project.user_name || "Unknown User",
              },
              created_at: project.created_at || new Date().toISOString(),
            };
          });
          setProjects(formattedProjects);
        } else {
          console.error("API response is not an array:", response.data);
          setError("Invalid data format received from server");
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError(
          "Failed to fetch projects: " +
            (err.response?.data?.message || err.message)
        );
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const filteredProjects = projects
    .filter((project) => {
      const projectName = project.name || "";
      const projectStatus = project.status || "active";

      const matchesSearch = projectName
        .toString()
        .toLowerCase()
        .includes((searchQuery || "").toLowerCase());

      const matchesStatus =
        filterStatus === "all" || projectStatus === filterStatus;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortBy === "date") {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      return 0;
    });

  const handleProjectClick = (projectId, e) => {
    if (e && e.target && e.target.closest(".actions")) return;
    onProjectClick(projectId);
  };

  const handleDelete = async (projectId) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await api.delete(`/projects/${projectId}`);
        setProjects(
          projects.filter((project) => project.project_id !== projectId)
        );
      } catch (err) {
        console.error("Delete error:", err);
        alert(
          "Failed to delete project: " +
            (err.response?.data?.message || err.message)
        );
      }
    }
  };

  if (loading) return <div className="loading">Loading projects...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="manage-projects">
      <Navbar role={role} />
      <div className="container">
        <div className="header">
          <h1>Manage Projects</h1>
          <button onClick={onCreateProject} className="secondary-button">
            <Plus size={20} /> New Project
          </button>
        </div>

        <div className="controls">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="all" key="status-all">
              All Status
            </option>
            <option value="active" key="status-active">
              Active
            </option>
            <option value="completed" key="status-completed">
              Completed
            </option>
            <option value="on-hold" key="status-on-hold">
              On Hold
            </option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort by"
          >
            <option value="name" key="sort-name">
              Sort by Name
            </option>
            <option value="date" key="sort-date">
              Sort by Date
            </option>
          </select>
        </div>

        <div className="projects-grid">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <ProjectCard
                key={project.project_id}
                project={project}
                onDelete={handleDelete}
                onClick={(e) => handleProjectClick(project.project_id, e)}
                showCreator={true}
              />
            ))
          ) : (
            <div className="no-projects-message">
              {searchQuery || filterStatus !== "all"
                ? "No matching projects found"
                : "No projects available. Click 'New Project' to create one."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageProjects;
